/**
 * Manage assignments component
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WdLocalEnvironmentService } from '../../../../../services/environment/wd-local-environment.service';
import { NgForm } from '@angular/forms';
import { WdVehicleAssignmentService } from '../../../../../services/vehicles/assignments/wd-vehicle-assignment.service';
import { IBcxErrorService } from '../../../../../interfaces/generic/generic-interfaces';
import { IVehicleAssignmentInputModel } from '../../../../../interfaces/vehicles/vehicle-interfaces';
import { WdLocalSharedFunctionsService } from '../../../../../services/shared/wd-local-shared-functions.service';

@Component({
  selector: 'app-manage-assignments',
  templateUrl: './manage-assignments.component.html',
  styleUrls: ['./manage-assignments.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ManageAssignmentsComponent implements OnInit, AfterViewInit {
  // Form
  @ViewChild('assignmentsForm', { static: true })
  public assignmentsForm!: NgForm;

  // Inputs
  assignment: IVehicleAssignmentInputModel;
  assignmentUntouched: string;

  // Booleans
  isChanges = false;
  isEditMode = false;
  isLoading = false;
  isSaving = false;

  // Callbacks
  onSuccess: any;

  /**
   * Constructor
   * @since 4.70.0
   */
  constructor(
    public changeDetector: ChangeDetectorRef,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdVehicleAssignmentService: WdVehicleAssignmentService,
    public dialogRef: MatDialogRef<ManageAssignmentsComponent>,
    public wdEnvironmentService: WdLocalEnvironmentService,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  /**
   * Initialize Assignment
   */
  initializeAssignment(assignment: any) {
    this.isEditMode = !!assignment?.id;
    const selectedDriver = assignment
      ? this.wdVehicleAssignmentService.vehicleAssignmentRequiredData.value.userOptions?.find(
          (item: any) => item.id === assignment.driver?.id,
        )
      : null;
    const selectedVehicle = assignment
      ? this.wdVehicleAssignmentService.vehicleAssignmentRequiredData.value.vehicleOptions.find(
          (item: any) => item.id === assignment.vehicle?.id,
        )
      : null;
    const orders = assignment ? assignment.orders : [];

    return {
      id: assignment ? assignment.id : null,
      dateStart: assignment ? new Date(assignment.date?.start) : null,
      dateEnd: assignment ? new Date(assignment.date?.end) : null,
      vehicle: selectedVehicle,
      driver: selectedDriver,
      orders: orders,
      notes: assignment ? assignment.notes : null,
    };
  }

  /**
   * On Changes to Inputs
   * @since 4.70.0
   */
  checkForChanges(): void {
    this.isChanges =
      this.assignmentUntouched !== JSON.stringify(this.assignment);
  }

  /**
   * On Init
   * @since 4.70.0
   */
  async ngOnInit() {
    // Start Loader
    this.isLoading = true;

    // Load required data
    let assignment = this.data?.assignment;
    const assignmentId = assignment?.id;
    if (assignmentId) {
      // Load assignment by ID
      assignment = await this.wdVehicleAssignmentService.fetchVehicleAssignmentManageData(
        assignmentId,
      );
    } else {
      // Only load required data for assignments
      await this.wdVehicleAssignmentService.fetchVehicleAssignmentManageData();
    }

    // Set defaults and capture changes
    this.assignment = this.initializeAssignment(assignment);
    this.assignmentUntouched = JSON.stringify(assignment);

    // Functions
    this.onSuccess = this.data.onSuccessCallback;

    // Stop Loader
    this.isLoading = false;
  }

  /**
   * On Modal Submit
   * @since 4.70.0
   */
  async onSubmit(assignment: any): Promise<void> {
    // Start Loader
    this.isLoading = true;
    try {
      this.isEditMode
        ? await this.wdVehicleAssignmentService.updateVehicleAssignment(
            assignment,
          )
        : await this.wdVehicleAssignmentService.addVehicleAssignment(
            assignment,
          );
      // Close the dialog, return false
      this.onSuccess();
      this.dialogRef.close(false);
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * On Modal Dismiss
   * @since 4.70.0
   */
  onDismiss(): void {
    // Close the dialog, return false
    this.dialogRef.close(false);
  }

  /**
   * After View Init
   * @since 4.70.0
   */
  ngAfterViewInit() {
    // clear anything at the parent form level
    this.assignmentsForm?.reset(this.assignment);
    // Detect Changes
    this.changeDetector.detectChanges();
  }
}
